import { Box } from '@ironhack/design-system2/components/box';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import {
  type BackgroundColor,
  backgroundToTokens,
} from '@ironhack/design-system2/utils';
import { type ReactElement, type RefObject } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { Button } from '@/app/components/client/button';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import {
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms';
import { type WithTracking } from '@/types';
import { NurturingForm, type NurturingFormType } from './nurturing-form';

export type RoundedBanner = {
  background?: BackgroundColor;
  button: [WithTracking<DatoButtonBlock>];
  content: StructuredTextGraphQlResponse;
  headingProps?: TextProps;
  logos?: DatoImage[];
  image?: DatoImage;
  moduleFormat: [DatoModuleFormat];
  nurturingForm?: [{ form: NurturingFormType }];
  title: string;
};

type Props = {
  data: RoundedBanner;
  ref?: RefObject<HTMLDivElement>;
} & FlexProps;

export const RoundedBanner = (props: Props): ReactElement => {
  const { data, ref, title: navTitle, ...flexProps } = props;
  const {
    background = 'brand',
    button: [button] = [],
    content,
    headingProps = {},
    image,
    logos,
    moduleFormat: [moduleFormat],
    nurturingForm: [nurturingForm] = [],
    title,
  } = data;
  const formData = nurturingForm?.form;

  const { bg, color } = backgroundToTokens(background);

  return (
    <ModuleWrapper
      innerProps={flexProps}
      moduleFormat={moduleFormat}
      ref={ref}
      // Send title directly for NavigationTab
      title={navTitle}
    >
      <Flex
        alignItems="center"
        bg={bg}
        borderRadius="16px"
        color={color}
        flexDirection={['column', null, 'row']}
        px={[2, null, 4]}
        py={[3, null, 4]}
        w="full"
      >
        {image && (
          <Box
            borderRadius="8px"
            maxW={['100%', null, '590px']}
            mb={[2, null, null]}
            mr={[null, null, 4]}
            overflow="hidden"
          >
            <CmsImage data={image.responsiveImage} />
          </Box>
        )}
        <Flex direction="column" maxW="full">
          <Text as="h2" textStyle={['3xl', null, '4xl']} {...headingProps}>
            {title}
          </Text>
          <StructuredContent color={color} data={content} maxW="65ch" mt={2} />
          {formData?.active && <NurturingForm color={color} data={formData} />}
          {logos && logos.length > 0 && (
            <Flex gap={1.5} mt={4} wrap="wrap">
              {logos.map((logo) => (
                <Box
                  borderColor="white"
                  borderRadius="full"
                  borderWidth="2px"
                  flexShrink="0"
                  key={logo.responsiveImage.alt}
                  p={0.5}
                >
                  <CmsImage data={logo.responsiveImage} sizes="36px" />
                </Box>
              ))}
            </Flex>
          )}
          {!formData?.active && button && (
            <Button w={['full', null, 'fit-content']} {...button} mt={4} />
          )}
        </Flex>
      </Flex>
    </ModuleWrapper>
  );
};

export const roundedBannerFragment = `
  fragment roundedBannerFragment on RoundedBannerRecord {
    __typename
    button {
      ...buttonFragment
    }
    content {
      value
    }
    logos {
      responsiveImage(
        imgixParams: {
          fit: crop,
          q: 45,
          w: 36,
          h: 36,
          auto: format,
          mask: ellipse
        }
      ) {
        ...responsiveImageFragment
      }
    }
    image {
      responsiveImage {
        ...responsiveImageFragment
      }
    }
    moduleFormat {
      ...moduleFormatFragment
    }
    nurturingForm {
      form {
        active
        buttonText
        emailLabel
        emailPlaceholder
        emailValidationMessages {
          invalidMessage
          lengthMessage
          requiredMessage
        }
        errorMessage
        legalText {
          blocks
          links
          value
        }
        newsletterLabel
        newsletterEnabled
        salesforceSource
        successMessage
      }
    }
    title
  }
`;
